/**
 * LES CODES, MIS EN FRANÇAIS — ET C'EST LE SEUL ENDROIT OÙ ÇA SE FAIT.
 *
 * L'API ne renvoie que des codes machine (`EMAIL_DEJA_PRIS`, `IPE_TROP_BASSE`…). C'est la
 * convention du dépôt, et sa raison est simple : une phrase française renvoyée par le serveur est
 * une phrase qu'on ne pourra pas traduire au lot 8. Ici, une table ; là-bas, la même table en
 * anglais, et rien d'autre à changer.
 *
 * Les phrases DISENT QUOI FAIRE quand il y a quelque chose à faire. « TROP_LOURD » ne devient pas
 * « trop lourd » mais « votre sac ne le portera pas — posez quelque chose » : un message qui
 * constate est un message que le joueur doit interpréter tout seul.
 */

import { t } from "./langue.js";

const PHRASES_FR: Record<string, string> = {
  /* Le compte */
  EMAIL_DEJA_PRIS: "Cette adresse a déjà un compte. Connectez-vous plutôt.",
  EMAIL_INVALIDE: "Cette adresse ne ressemble pas à une adresse.",
  MOT_DE_PASSE_TROP_COURT: "Il faut au moins dix caractères — c'est la seule chose qui protège vos héros.",
  IDENTIFIANTS_INVALIDES: "Ni cette adresse ni ce mot de passe ne conviennent.",
  NON_AUTHENTIFIE: "Votre session s'est refermée. Reconnectez-vous.",
  INTERDIT: "Ceci ne vous appartient pas.",
  INTROUVABLE: "Il n'y a rien ici.",

  /* Les héros */
  NOM_INVALIDE: "Un nom, entre deux et trente-deux caractères.",
  ARCHETYPE_INCONNU: "Cet archétype n'existe pas.",
  POINTS_INVALIDES: "Cette répartition ne tient pas : huit points, quatre au plus sur une même caractéristique.",
  POINTS_NON_DEPENSES: "Il reste des points. Ils ne se garderont pas : il n'y a pas de « plus tard » où les dépenser.",
  PLAFOND_DEPASSE: "Quatre points au maximum sur une seule caractéristique.",
  TROP_DE_HEROS: "Votre abonnement ne tient pas un héros vivant de plus. Retirez-en un, ou attendez qu'il meure.",
  HEROS_INDISPONIBLE: "Ce héros n'est plus en état de partir.",
  CARACTERISTIQUE_INCONNUE: "Cette caractéristique n'existe pas.",
  TROP_DE_CICATRICES: "Ce corps ne peut plus porter de cicatrice. La Chronique s'arrête ici.",

  /* Entrer dans une aventure */
  IPE_TROP_HAUTE: "Vous êtes trop bien équipé pour cette aventure. Ce ne serait pas un défi, ce serait une visite.",
  IPE_TROP_BASSE: "Votre équipement est en dessous de ce que cette aventure demande. Vous pouvez entrer quand même, par pacte.",
  MODE_INCONNU: "Ce mode de jeu n'existe pas.",
  MODE_RESERVE_AUX_ABONNES: "La Chronique demande un abonnement. En permadeath, la mort est définitive — c'est le jeu tel qu'il est écrit.",
  OPTION_HERITAGE_MANQUANTE: "L'Héritage se dépense au départ, et vous n'en avez plus au coffre. Il s'achète à l'échoppe.",
  PARTIE_DEJA_EN_COURS: "Ce héros est déjà en aventure. On ne se dédouble pas.",
  AVENTURE_SANS_DEPART: "Cette aventure n'a pas de premier nœud : elle est inutilisable.",

  /* La partie */
  PARTIE_CLOSE: "Cette partie est terminée.",
  REPRISE_EN_ATTENTE: "Votre héros est tombé. Désignez la cicatrice qu'il gardera avant de repartir.",
  AUCUNE_REPRISE_EN_ATTENTE: "Rien à reprendre ici.",
  AUCUNE_ANCRE: "Aucune ancre où revenir.",
  PARTIE_SANS_NOEUD: "Cette partie a perdu son nœud courant.",
  NOEUD_INTROUVABLE: "Ce nœud n'existe pas dans cette aventure.",
  CHOIX_VERROUILLE: "Il vous manque quelque chose pour ce chemin.",
  RENCONTRE_EN_ATTENTE: "Quelqu'un vous barre le passage. On ne s'en va pas, et on ne s'attarde pas : il faut en découdre.",
  AUCUNE_RENCONTRE: "Il n'y a personne à affronter ici.",

  /* La fouille (§3.6) */
  CACHE_DEJA_OUVERTE: "Vous l'avez déjà fouillée.",
  CACHE_VERROUILLEE: "C'est fermé, et vous n'avez pas la clef. Notez l'endroit : elle est quelque part dans cette région.",
  REPONSE_FAUSSE: "Ce n'est pas le mot. Vous y avez perdu une heure — mais l'inscription ne s'efface pas.",
  ENIGME_INTROUVABLE: "L'inscription est illisible. Signalez-le : ce n'est pas de votre fait.",
  CACHE_FERMEE: "Il faut d'abord l'ouvrir.",
  OBJET_ABSENT_DE_LA_CACHE: "Il n'y a plus ça dedans.",
  LIEUX_DEJA_FOUILLES: "Vous avez déjà fouillé les lieux. Il n'y a rien de plus à trouver ici.",

  /* La carte annotée (§4.2) */
  HEXAGONE_NON_VU: "On n'écrit pas sur ce qu'on n'a pas vu. Allez-y d'abord, ou apercevez-le de loin.",
  MARQUEUR_INCONNU: "Ce marqueur n'existe pas.",
  NOTE_TROP_LONGUE: "Cent vingt signes au plus — une note de carte se lit d'un coup d'œil.",
  VOTE_POUR_SOI: "On ne vote pas pour sa propre note.",

  /* Le corps */
  PAS_DE_RATION: "Plus une seule ration.",
  RATIONS_INSUFFISANTES: "Pas assez de rations pour ce repos.",
  PAS_DE_KIT: "Un camp demande un kit de campement.",
  SANCTUAIRE_REQUIS: "Cela ne se fait que dans un sanctuaire. Il y en a un par région — la carte les garde en mémoire.",
  REPOS_INCONNU: "Ce repos n'existe pas.",

  /* Les parchemins (§3.7) */
  OBJET_NON_PARCHEMIN: "Cet objet ne se lit pas.",

  /* La lumière (§3.8) */
  OBJET_SANS_FLAMME: "Cela n'éclaire rien.",
  OBJET_SANS_SORT: "Cet objet ne porte aucun sort.",
  AUCUNE_FLAMME: "Vous n'avez rien d'allumé.",
  PARCHEMIN_HORS_COMBAT: "Cela se lit avant d'engager, pas au milieu des coups.",
  PARCHEMIN_DE_COMBAT: "Ce mot-là n'a de sens qu'en plein combat.",
  AUCUNE_BLESSURE: "Vous n'avez aucune blessure grave à refermer — gardez-le.",
  BLESSURE_ABSENTE: "Vous ne portez pas cette blessure-là.",
  OR_INSUFFISANT: "Le rebouteux ne travaille pas à crédit.",
  TROP_LOURD: "Votre dos ne portera pas ça. Posez quelque chose.",
  SLOTS_INSUFFISANTS: "Votre sac est plein. Posez quelque chose, ou trouvez un contenant.",
  OBJET_ABSENT: "Vous n'avez pas cet objet.",
  OBJET_INCONNU: "Cet objet n'existe pas.",
  OBJET_NON_CONSOMMABLE: "Cet objet ne se consomme pas.",
  EMPLACEMENT_INVALIDE: "Cet objet ne se porte pas là.",
  EMPLACEMENT_VIDE: "Il n'y a rien à cet emplacement.",
  MAINS_OCCUPEES: "Vos deux mains sont déjà prises par cette arme.",
  QUANTITE_INVALIDE: "Cette quantité n'a pas de sens.",

  /* Le combat */
  AUCUN_COMBAT: "Vous ne vous battez contre personne.",
  COMBAT_EN_COURS: "Pas pendant un combat.",
  COMBAT_TERMINE: "Ce combat est fini.",
  PAS_VOTRE_TOUR: "Ce n'est pas votre tour.",
  CIBLE_INVALIDE: "Cette cible n'est pas là.",
  ACTION_INCONNUE: "Cette action n'existe pas.",
  ESSENCE_INSUFFISANTE: "Pas assez d'Essence.",
  CAPACITE_EPUISEE: "Vous avez déjà usé de votre capacité dans ce combat.",
  CAPACITE_INCONNUE: "Votre archétype n'a pas cette capacité.",
  ETOURDI: "Étourdi : ce tour est perdu.",
  PAS_CACHE: "Il faut être caché pour cela.",
  PERSONNE_A_FUIR: "Il n'y a plus personne devant vous.",

  /* L'atelier */
  GENRE_INCONNU: "Ce genre n'est pas au catalogue.",
  NOEUDS_HORS_BORNES: "Une aventure fait entre huit cents et mille nœuds.",
  GENERATION_DEJA_EN_COURS: "Une aventure est déjà en fabrication pour vous.",
  GENERATION_ECHOUEE: "La fabrication s'est arrêtée. Rien n'a été facturé, vous pouvez relancer.",
  QUOTA_QUOTIDIEN_EPUISE: "Vous avez atteint votre quota du jour.",
  QUOTA_MENSUEL_EPUISE: "Votre quota du mois est épuisé.",
  AVENTURE_DEJA_IMPORTEE: "Cette aventure est déjà en base.",
  AVENTURE_INVALIDE: "Cette aventure ne passe pas le validateur.",
  AVENTURE_NON_TERMINABLE: "Le solveur n'a pas pu prouver qu'on peut la finir. Elle ne sera pas jouée.",
  AVENTURE_JOUEE: "Quelqu'un y joue en ce moment.",

  /* La voix (§7) */
  QUOTA_VOIX_EPUISE: "Vous avez écouté tout ce que le compte gratuit permet aujourd'hui. La lecture par le navigateur, elle, ne se compte pas.",

  /* L'échoppe, le coffre et la caisse (§8) */
  ARTICLE_INCONNU: "Cet article n'est pas à l'étal.",
  FRAGMENTS_INSUFFISANTS: "Pas assez de fragments. On en gagne en terminant une aventure — vingt — et en abattant un boss — cent.",
  COFFRE_PLEIN: "Votre coffre n'a plus une ligne de libre. Videz-en une, ou achetez une étagère de plus. Rien n'a été débité.",
  COFFRE_INSUFFISANT: "Le coffre n'en contient pas autant.",
  PARTIE_EN_COURS: "Ce héros est en route. On prépare son héros avant de partir, on assume ensuite.",
  HEROS_MORT: "Ce héros est mort. Son coffre reste au compte ; lui, non.",
  OPTION_NON_TRANSPORTABLE: "Une option d'aventure ne se porte pas : elle se dépense au départ.",
  PRODUIT_INCONNU: "Cette formule n'existe pas.",
  PAIEMENT_INDISPONIBLE: "La caisse n'est pas ouverte pour le moment. Rien n'a été débité.",
  PAIEMENT_REFUSE: "Le paiement n'a pas abouti. Rien n'a été débité.",
  SIGNATURE_INVALIDE: "Cette confirmation de paiement n'est pas authentique.",

  /* Le transport */
  // HORS_LIGNE ne vient pas du serveur, par construction : c'est le client d'API qui le pose quand
  // un appel n'a eu aucune réponse HTTP. Il dit ce qui s'est passé, et surtout ce qui n'a PAS eu
  // lieu — rien n'a été joué, donc il n'y a rien à défaire.
  HORS_LIGNE:
    "Le serveur n'est pas joignable. Rien n'a été joué : ce qui est à l'écran attend que la connexion revienne.",
  JSON_ILLISIBLE: "La requête n'était pas lisible.",
  ERREUR_INTERNE: "Quelque chose a cassé de notre côté. Rien n'est perdu : rechargez la page.",
  ERREUR_INCONNUE: "Quelque chose a cassé. Rien n'est perdu : rechargez la page."
};

/** LES PHRASES EN FRANÇAIS, telles quelles : le test de couverture les parcourt pour exiger qu'il
 * n'en manque aucune à l'anglais. C'est la seule raison pour laquelle elles sont exportées. */
export const PHRASES_EN_FRANCAIS = PHRASES_FR;

/**
 * LE CODE MIS EN PHRASE, dans la langue du lecteur (§13.3).
 *
 * Elle s'appelait `enFrancais`, ce qui était vrai tant qu'il n'y avait qu'une langue. Le nom
 * mentait dès la seconde : ce qu'elle fait, c'est mettre un code machine en phrase — et laquelle
 * dépend de qui lit. Le code inconnu s'affiche entre parenthèses : c'est ce que le joueur nous
 * recopiera dans un rapport de bogue, et c'est plus utile qu'un « une erreur est survenue ».
 */
export function enPhrase(code: string): string {
  const francais = PHRASES_FR[code];
  return francais ? t(francais) : t("Quelque chose a été refusé ({code}).", { code });
}

/** L'ancien nom, gardé le temps que les appelants passent au nouveau. */
export const enFrancais = enPhrase;

export function messageDe(erreur: unknown): string {
  if (erreur && typeof erreur === "object" && "code" in erreur && typeof erreur.code === "string") {
    return enPhrase(erreur.code);
  }
  if (erreur instanceof Error && erreur.message) return t("Le serveur n'a pas répondu. Vérifiez votre connexion.");
  return enPhrase("ERREUR_INCONNUE");
}
